import React, { useState, useEffect, useMemo } from 'react';
import { getAccountsReceivable } from '../api';
import { useTheme } from '../hooks/useTheme';

const fmt = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const agingColor = (days) => {
  if (days == null) return undefined;
  if (days <= 30) return '#22c55e';  // green
  if (days <= 60) return '#f59e0b';  // amber
  if (days <= 90) return '#f97316';  // orange
  return '#ef4444';                  // red
};

const agingLabel = (days) => {
  if (days == null) return '—';
  if (days <= 0) return 'Current';
  return `${days}d`;
};

const ARPage = ({ onOpenProject, currentUser }) => {
  const t = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterProject, setFilterProject] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [sortField, setSortField] = useState('aging_days');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAccountsReceivable();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Unique project/client lists for filters
  const projectOptions = useMemo(() => {
    if (!data) return [];
    const set = new Map();
    data.periods.forEach(p => set.set(p.project_id, `${p.project_number || ''} ${p.project_name}`.trim()));
    return [...set.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const clientOptions = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.periods.map(p => p.client_name).filter(Boolean));
    return [...set].sort();
  }, [data]);

  // Filtered and sorted rows
  const rows = useMemo(() => {
    if (!data) return [];
    let list = [...data.periods];
    if (filterProject) list = list.filter(r => String(r.project_id) === filterProject);
    if (filterClient) list = list.filter(r => r.client_name === filterClient);
    list.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') av = av || '';
      if (typeof bv === 'string') bv = bv || '';
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [data, filterProject, filterClient, sortField, sortDir]);

  // Filtered totals
  const filteredTotals = useMemo(() => {
    return {
      billed: rows.reduce((s, r) => s + r.total_billed, 0),
      retainage: rows.reduce((s, r) => s + r.total_retainage, 0),
      gross: rows.reduce((s, r) => s + r.total_completed_and_stored, 0),
    };
  }, [rows]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortArrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>
        Loading accounts receivable...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>
        Error: {error}
        <br />
        <button onClick={loadData} style={{ marginTop: 12, padding: '6px 16px', cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, aging_buckets } = data;
  const bucketTotal = aging_buckets.current + aging_buckets.days31_60 + aging_buckets.days61_90 + aging_buckets.over90;

  const cardStyle = {
    background: t.bgAlt,
    border: `1px solid ${t.cardBorder}`,
    borderRadius: 8,
    padding: '16px 20px',
    flex: 1,
    minWidth: 150,
  };

  const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: t.tableHeaderColor,
    background: t.tableHeaderBg,
    borderBottom: `2px solid ${t.tableBorder}`,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '10px 12px',
    fontSize: 13,
    color: t.text,
    borderBottom: `1px solid ${t.tableBorder}`,
  };

  return (
    <div style={{ padding: '0 8px' }}>
      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>
            Total Outstanding
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: t.heading }}>
            {fmt(summary.total_billed)}
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
            {summary.count} invoice{summary.count !== 1 ? 's' : ''}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>
            Retainage Held
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f59e0b' }}>
            {fmt(summary.total_retainage)}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>
            Gross Earned
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#3b82f6' }}>
            {fmt(summary.total_completed_and_stored)}
          </div>
        </div>
      </div>

      {/* Aging Buckets Bar */}
      <div style={{ ...cardStyle, marginBottom: 24, flex: 'none' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', marginBottom: 10 }}>
          Aging Summary
        </div>
        <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', height: 28, marginBottom: 10 }}>
          {[
            { label: 'Current (0-30)', amount: aging_buckets.current, color: '#22c55e' },
            { label: '31-60 days', amount: aging_buckets.days31_60, color: '#f59e0b' },
            { label: '61-90 days', amount: aging_buckets.days61_90, color: '#f97316' },
            { label: '90+ days', amount: aging_buckets.over90, color: '#ef4444' },
          ].map((bucket, i) => {
            const pct = bucketTotal > 0 ? (bucket.amount / bucketTotal) * 100 : 0;
            if (pct === 0) return null;
            return (
              <div
                key={i}
                title={`${bucket.label}: ${fmt(bucket.amount)}`}
                style={{
                  width: `${pct}%`,
                  background: bucket.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#fff',
                  minWidth: pct > 5 ? 'auto' : 0,
                  overflow: 'hidden',
                }}
              >
                {pct >= 12 ? fmt(bucket.amount) : ''}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 20, fontSize: 11, color: t.textMuted }}>
          {[
            { label: 'Current (0-30)', amount: aging_buckets.current, color: '#22c55e' },
            { label: '31-60 days', amount: aging_buckets.days31_60, color: '#f59e0b' },
            { label: '61-90 days', amount: aging_buckets.days61_90, color: '#f97316' },
            { label: '90+ days', amount: aging_buckets.over90, color: '#ef4444' },
          ].map((bucket, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: bucket.color, display: 'inline-block' }} />
              {bucket.label}: {fmt(bucket.amount)}
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={filterProject}
          onChange={e => setFilterProject(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: 12, borderRadius: 6,
            border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.inputColor,
          }}
        >
          <option value="">All Projects</option>
          {projectOptions.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>

        <select
          value={filterClient}
          onChange={e => setFilterClient(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: 12, borderRadius: 6,
            border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.inputColor,
          }}
        >
          <option value="">All Clients</option>
          {clientOptions.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        {(filterProject || filterClient) && (
          <button
            onClick={() => { setFilterProject(''); setFilterClient(''); }}
            style={{
              padding: '6px 12px', fontSize: 11, cursor: 'pointer', borderRadius: 6,
              border: `1px solid ${t.inputBorder}`, background: 'transparent', color: t.textMuted,
            }}
          >
            Clear Filters
          </button>
        )}

        <div style={{ marginLeft: 'auto', fontSize: 12, color: t.textMuted }}>
          Showing {rows.length} of {data.periods.length} invoices
          {(filterProject || filterClient) ? ` — Filtered total: ${fmt(filteredTotals.billed)}` : ''}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', border: `1px solid ${t.tableBorder}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: t.tableBg }}>
          <thead>
            <tr>
              <th style={thStyle} onClick={() => handleSort('project_name')}>
                Project{sortArrow('project_name')}
              </th>
              <th style={thStyle} onClick={() => handleSort('client_name')}>
                Client{sortArrow('client_name')}
              </th>
              <th style={thStyle} onClick={() => handleSort('period_name')}>
                AFP{sortArrow('period_name')}
              </th>
              <th style={thStyle} onClick={() => handleSort('invoice_date')}>
                Invoice Date{sortArrow('invoice_date')}
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('total_completed_and_stored')}>
                Gross Earned{sortArrow('total_completed_and_stored')}
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('total_retainage')}>
                Retainage{sortArrow('total_retainage')}
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('total_billed')}>
                Net Billed{sortArrow('total_billed')}
              </th>
              <th style={{ ...thStyle, textAlign: 'center' }} onClick={() => handleSort('aging_days')}>
                Age{sortArrow('aging_days')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', padding: 40, color: t.textMuted }}>
                  No finalized billing periods found.
                </td>
              </tr>
            ) : rows.map(row => (
              <tr
                key={row.period_id}
                style={{ cursor: onOpenProject ? 'pointer' : 'default' }}
                onMouseEnter={e => e.currentTarget.style.background = t.tableRowHover}
                onMouseLeave={e => e.currentTarget.style.background = ''}
                onClick={() => onOpenProject && onOpenProject(row.project_id)}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{row.project_name}</div>
                  {row.project_number && (
                    <div style={{ fontSize: 11, color: t.textMuted }}>{row.project_number}</div>
                  )}
                </td>
                <td style={tdStyle}>{row.client_name || '—'}</td>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 500 }}>{row.period_name}</span>
                  {row.revised_from_id && (
                    <span style={{
                      marginLeft: 6, fontSize: 9, fontWeight: 600, padding: '1px 5px',
                      borderRadius: 3, background: '#3b82f620', color: '#3b82f6',
                    }}>
                      REV
                    </span>
                  )}
                </td>
                <td style={tdStyle}>
                  {row.invoice_date || '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
                  {fmt(row.total_completed_and_stored)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: '#f59e0b' }}>
                  {fmt(row.total_retainage)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
                  {fmt(row.total_billed)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    background: agingColor(row.aging_days),
                    minWidth: 40,
                  }}>
                    {agingLabel(row.aging_days)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr style={{ background: t.totalsBg }}>
                <td colSpan={4} style={{ ...tdStyle, fontWeight: 700, borderTop: `2px solid ${t.tableBorder}` }}>
                  Totals
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', fontSize: 12, borderTop: `2px solid ${t.tableBorder}` }}>
                  {fmt(filteredTotals.gross)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', fontSize: 12, color: '#f59e0b', borderTop: `2px solid ${t.tableBorder}` }}>
                  {fmt(filteredTotals.retainage)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', fontSize: 12, borderTop: `2px solid ${t.tableBorder}` }}>
                  {fmt(filteredTotals.billed)}
                </td>
                <td style={{ ...tdStyle, borderTop: `2px solid ${t.tableBorder}` }} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default ARPage;
